import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { queryKeys } from '@/shared/hooks/queries/queryKeys'
import {
  getGroupDrugs,
  addDrugsToGroup,
  removeDrugFromGroup,
  bulkDeleteDrugsFromGroup,
  getAllNormalDrugs,
  getAllAdvancedDrugs,
} from '@/features/internal/drugs/apis/soapTemplate'
import {
  GroupDrug,
  AddDrugsToGroupRequest,
  Drug,
} from '@/features/internal/drugs/types/soapTemplate'
import { AdvancedSearchCondition } from '@/shared/types/advancedSearch'
import { useToast } from '@/shared/contexts/ToastContext'
import { AxiosError } from 'axios'

// ========================================
// useGroupDrugsQuery
// ========================================

interface UseGroupDrugsQueryReturn {
  groupDrugs: GroupDrug[]
  data: GroupDrug[] | undefined
  isLoading: boolean
  isError: boolean
  error: Error | null
  refetch: () => void
  isFetching: boolean
  isSuccess: boolean
}

/**
 * グループ所属医薬品一覧取得クエリ（TanStack Query）
 *
 * 責務:
 * - 医薬品グループに所属する医薬品一覧の取得
 * - キャッシュ管理
 *
 * 使用例:
 * ```tsx
 * const { groupDrugs, isLoading } = useGroupDrugsQuery('group-id')
 * ```
 */
export const useGroupDrugsQuery = (
  groupId: string | undefined
): UseGroupDrugsQueryReturn => {
  const { showToast } = useToast()

  const query = useQuery({
    queryKey: queryKeys.drugGroups.drugs(groupId ?? ''),
    queryFn: async (): Promise<GroupDrug[]> => {
      if (!groupId || groupId === 'create') return []
      const response = await getGroupDrugs(groupId)
      return response.data
    },
    enabled: !!groupId && groupId !== 'create',
    staleTime: 1000 * 60 * 5, // 5分キャッシュ
    meta: {
      onError: (error: AxiosError) => {
        // 404以外のエラーのみトースト表示
        if (error.response?.status !== 404) {
          const errorMessage =
            (error.response?.data as { detail?: string })?.detail ||
            '所属医薬品の取得に失敗しました'
          showToast(errorMessage, 'error')
        }
      },
    },
  })

  return {
    groupDrugs: query.data ?? [],
    data: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
    isFetching: query.isFetching,
    isSuccess: query.isSuccess,
  }
}

// ========================================
// useAddDrugsToGroupMutation
// ========================================

interface AddDrugsToGroupVariables {
  groupId: string
  drugIds: string[]
}

/**
 * グループへの医薬品追加Mutation（TanStack Query）
 *
 * 責務:
 * - 医薬品グループへの医薬品追加
 * - 成功時にキャッシュinvalidate + Toast表示
 */
export const useAddDrugsToGroupMutation = () => {
  const queryClient = useQueryClient()
  const { showToast } = useToast()

  return useMutation({
    mutationFn: async ({
      groupId,
      drugIds,
    }: AddDrugsToGroupVariables): Promise<void> => {
      const data: AddDrugsToGroupRequest = { drug_ids: drugIds }
      await addDrugsToGroup(groupId, data)
    },
    onSuccess: (_, variables) => {
      // グループの医薬品一覧キャッシュをinvalidate
      queryClient.invalidateQueries({
        queryKey: queryKeys.drugGroups.drugs(variables.groupId),
      })
      showToast('医薬品をグループに追加しました', 'success')
    },
    onError: (error: AxiosError) => {
      const errorMessage =
        (error.response?.data as { detail?: string })?.detail ||
        '医薬品のグループへの追加に失敗しました'
      showToast(errorMessage, 'error')
    },
  })
}

// ========================================
// useRemoveDrugFromGroupMutation
// ========================================

interface RemoveDrugFromGroupVariables {
  groupId: string
  drugId: string
}

/**
 * グループからの医薬品削除Mutation（TanStack Query）
 *
 * 責務:
 * - 医薬品グループからの医薬品削除
 * - 成功時にキャッシュinvalidate + Toast表示
 */
export const useRemoveDrugFromGroupMutation = () => {
  const queryClient = useQueryClient()
  const { showToast } = useToast()

  return useMutation({
    mutationFn: async ({
      groupId,
      drugId,
    }: RemoveDrugFromGroupVariables): Promise<void> => {
      await removeDrugFromGroup(groupId, drugId)
    },
    onSuccess: (_, variables) => {
      // グループの医薬品一覧キャッシュをinvalidate
      queryClient.invalidateQueries({
        queryKey: queryKeys.drugGroups.drugs(variables.groupId),
      })
      showToast('医薬品をグループから削除しました', 'success')
    },
    onError: (error: AxiosError) => {
      const errorMessage =
        (error.response?.data as { detail?: string })?.detail ||
        '医薬品のグループからの削除に失敗しました'
      showToast(errorMessage, 'error')
    },
  })
}

// ========================================
// useBulkDeleteDrugsFromGroupMutation
// ========================================

interface BulkDeleteDrugsFromGroupVariables {
  groupId: string
  drugIds: string[]
}

interface BulkDeleteResponse {
  deleted_count: number
  remaining_drugs: GroupDrug[]
}

/**
 * グループからの医薬品一括削除Mutation（TanStack Query）
 *
 * 責務:
 * - 医薬品グループからの複数医薬品一括削除
 * - 成功時にキャッシュ更新 + Toast表示
 */
export const useBulkDeleteDrugsFromGroupMutation = () => {
  const queryClient = useQueryClient()
  const { showToast } = useToast()

  return useMutation({
    mutationFn: async ({
      groupId,
      drugIds,
    }: BulkDeleteDrugsFromGroupVariables): Promise<BulkDeleteResponse> => {
      const response = await bulkDeleteDrugsFromGroup(groupId, drugIds)
      return response.data
    },
    onSuccess: (data, variables) => {
      // キャッシュを直接更新（remaining_drugsを使用）
      queryClient.setQueryData(
        queryKeys.drugGroups.drugs(variables.groupId),
        data.remaining_drugs
      )
      showToast(
        `${data.deleted_count}件の医薬品をグループから削除しました`,
        'success'
      )
    },
    onError: (
      error: AxiosError<{ error?: string; invalid_drug_ids?: string[] }>
    ) => {
      let errorMessage = '医薬品の一括削除に失敗しました'

      if (error.response?.data?.error) {
        errorMessage = error.response.data.error

        // 無効な医薬品IDがある場合の詳細表示
        if (error.response.data.invalid_drug_ids) {
          errorMessage += `\n無効なID: ${error.response.data.invalid_drug_ids.join(
            ', '
          )}`
        }
      }

      showToast(errorMessage, 'error')
    },
  })
}

// ========================================
// useFetchAllNormalDrugsMutation
// ========================================

interface FetchAllNormalDrugsResult {
  drugs: Drug[]
  count: number
}

/**
 * 通常検索で全件取得するMutation（TanStack Query）
 *
 * 責務:
 * - 検索キーワードに一致する全医薬品を取得
 * - 「全て選択」機能で使用
 *
 * 使用例:
 * ```tsx
 * const mutation = useFetchAllNormalDrugsMutation()
 * const result = await mutation.mutateAsync('カルボ')
 * ```
 */
export const useFetchAllNormalDrugsMutation = () => {
  const { showToast } = useToast()

  return useMutation({
    mutationFn: async (
      searchKeyword: string
    ): Promise<FetchAllNormalDrugsResult> => {
      const response = await getAllNormalDrugs(searchKeyword)
      return {
        drugs: response.data.results,
        count: response.data.count,
      }
    },
    onError: () => {
      showToast('すべての検索結果の取得に失敗しました', 'error')
    },
  })
}

// ========================================
// useFetchAllAdvancedDrugsMutation
// ========================================

interface FetchAllAdvancedDrugsResult {
  drugs: Drug[]
  count: number
}

/**
 * 高度検索で全件取得するMutation（TanStack Query）
 *
 * 責務:
 * - 高度検索条件に一致する全医薬品を取得
 * - 「全て選択」機能で使用
 *
 * 使用例:
 * ```tsx
 * const mutation = useFetchAllAdvancedDrugsMutation()
 * const result = await mutation.mutateAsync(conditions)
 * ```
 */
export const useFetchAllAdvancedDrugsMutation = () => {
  const { showToast } = useToast()

  return useMutation({
    mutationFn: async (
      conditions: AdvancedSearchCondition[]
    ): Promise<FetchAllAdvancedDrugsResult> => {
      const response = await getAllAdvancedDrugs(conditions)
      return {
        drugs: response.data.results,
        count: response.data.count,
      }
    },
    onError: () => {
      showToast('高度検索結果の取得に失敗しました', 'error')
    },
  })
}
